import React from 'react';

// Components (Legacy)
import LegacyPasswordCriteria from '@/views/components/errors/LegacyPasswordCriteria';
import { FormattedMessageV2 } from '@/views/components/i18n';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Libraries
import classNames from 'classnames';

// Utils
import {
  passwordHasLowerCase,
  passwordHasMinLength,
  passwordHasNumber,
  passwordHasSpecialChar,
  passwordHasUpperCase,
  passwordMeetsComplexityReq,
  PASSWORD_MIN_COMPLEXITY_CHECKS,
} from '@/utils/auth';

type Props = {
  password: string;
  confirm: string;
};

const PasswordCriteriaV2 = ({ password, confirm }: Props) => {
  const getCriteriaClassName = (validator: (value: string) => boolean) => (validator(password) ? 'success' : 'error');

  // Criteria Class Names
  const hasMinLengthClassName = classNames('criteria', getCriteriaClassName(passwordHasMinLength));
  const meetsComplexityReqClassName = classNames('criteria', getCriteriaClassName(passwordMeetsComplexityReq));
  const valueAndConfirmMatchesClassName = classNames(
    'criteria',
    getCriteriaClassName(value => !!value && value === confirm),
  );

  // Sub-Criteria Class Names
  const hasLowerCaseClassName = classNames('criteria sub-criteria', getCriteriaClassName(passwordHasLowerCase));
  const hasNumberClassName = classNames('criteria sub-criteria', getCriteriaClassName(passwordHasNumber));
  const hasSpecialCharClassName = classNames('criteria sub-criteria', getCriteriaClassName(passwordHasSpecialChar));
  const hasUpperCaseClassName = classNames('criteria sub-criteria', getCriteriaClassName(passwordHasUpperCase));

  return (
    <div className="password-criteria">
      <div className="criteria-container">
        <FormattedMessageV2 className={hasMinLengthClassName} toLocalize="Auth.SetPassword.Criteria.Length" />
        <FormattedMessageV2 className={valueAndConfirmMatchesClassName} toLocalize="Auth.SetPassword.Criteria.Match" />
      </div>

      <div className="criteria-container">
        <FormattedMessageV2
          className={meetsComplexityReqClassName}
          toLocalize="Auth.SetPassword.Criteria.ComplexityRequirements"
          values={{ count: PASSWORD_MIN_COMPLEXITY_CHECKS }}
        />
        <div className="sub-criteria-container">
          <FormattedMessageV2 className={hasLowerCaseClassName} toLocalize="Auth.SetPassword.Criteria.LowerCase" />
          <FormattedMessageV2 className={hasUpperCaseClassName} toLocalize="Auth.SetPassword.Criteria.UpperCase" />
          <FormattedMessageV2 className={hasNumberClassName} toLocalize="Auth.SetPassword.Criteria.Number" />
          <FormattedMessageV2 className={hasSpecialCharClassName} toLocalize="Auth.SetPassword.Criteria.SpecialChar" />
        </div>
      </div>
    </div>
  );
};

const PasswordCriteria: React.FC<Props> = props => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <PasswordCriteriaV2 {...props} />;
  }

  return <LegacyPasswordCriteria {...props} />;
};

export default PasswordCriteria;
